<template>
  <div class="lAuth -login">
    <img :src="logo.src" :alt="logo.alt" class="lAuth_logo">

    <h2 class="lAuth_title">{{$t('signIn')}}</h2>

    <as-notice :notice="notice" />

    <v-form
      as="div"
      class="lAuth_form"
      v-slot="{ errors }"
    >
      <v-field
        name="email"
        :label="$t('email')"
        v-model.trim="username"
        rules="required|email"
        v-slot="{ errors: fErrors }"
      >
        <as-input
          name="email"
          :label="$t('email')"
          type="email"
          v-model.trim="username"
          :placeholder="$t('input.email')"
          :error="getFirstError('email', fErrors)"
          @keyup.enter="submit(errors)"
        />
      </v-field>

      <v-field
        name="password"
        :label="$t('password')"
        v-model.trim="password"
        rules="required"
        v-slot="{ errors: fErrors }"
      >
        <as-input
          name="password"
          :label="$t('password')"
          v-model.trim="password"
          :append-icon="showPassword ? 'visibility' : 'visibilityOff'"
          :type="showPassword ? 'text' : 'password'"
          @click:append="showPassword = !showPassword"
          :placeholder="$t('input.password')"
          :error="getFirstError('password', fErrors)"
          @keyup.enter="submit(errors)"
        />
      </v-field>

      <button
        class="lAuth_button login_button"
        :class="{
          '-disabled': isLoginBtnDisabled
        }"
        :data-text="$t('signIn')"
        data-cy="login_btn"
        :disabled="isLoginBtnDisabled"
        @click="submit(errors)"
        type="submit"
      >
        {{$t('signIn')}}
        <span v-if="!save" class="lAuth_loader"></span>
      </button>

      <button
        v-if="showRestorePasswordButton"
        :data-text="$t('restorePassword')"
        data-analytics
        data-analytics-element-type="other"
        data-analytics-element-id="restore_password"
        :data-analytics-element-name="$t('restorePassword')"
        class="lAuth_button login_button"
        @click="goTo({ name: 'accessRecovery' })"
      >
        {{ $t('restorePassword') }}
      </button>
    </v-form>

    <div class="lAuth_linkGroup">
      <div
        class="lAuth_help"
        data-analytics
        data-analytics-element-type="other"
        data-analytics-element-id="restore_password"
        :data-analytics-element-name="$t('forgotPassword')"
        data-cy="restore_password_btn"
        @click="goTo({ name: 'accessRecovery' })"
      >
        {{ $t('forgotPassword') }}
      </div>

      <as-divider v-if="clients.length" class="lAuth_divider">{{ $t('partsSpeech.or') }}</as-divider>
      <as-types-auth
        class="lAuth_typesAuth"
        short-btn
        action="login"
      />
      <as-divider v-if="clients.length" class="lAuth_divider" />

      <a
        v-if="isRegistrationEnable"
        class="lAuth_register lAuth_link"
        @click="goTo({ name: 'signup' })"
      >
        {{ $t('register') }}
      </a>
    </div>
    <div
      v-if="!isRegistrationEnable && instructionUrl"
      @click="isShowInstruction = true"
      class="lAuth_register lAuth_link">
      {{ $t('userInstruction') }}
      <as-modal
        v-model="isShowInstruction"
        typeModal="fullSize"
      >
        <iframe :src="instructionUrl" style="width: 100%; height: 100%;"></iframe>
      </as-modal>
    </div>

    <as-support-auth />

    <as-modal
      :btnClose="true"
      v-model="isShowSupportModal"
    >
      <as-support-modal/>
    </as-modal>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'

import AsInput from '@web/components/AsInput/AsInput.vue'
import AsNotice from '@web/components/AsNotice.vue'
import AsModal from '@web/components/AsModal.vue'
import AsSupportModal from '@web/components/AsSupportNotAuth.vue'
import AsDivider from '@web/components/AsDivider.vue'
import AsTypesAuth from '@web/components/AsTypesAuth.vue'
import AsSupportAuth from '@web/components/AsSupportAuth.vue'

import i18n from '@web/plugins/i18n'

import useLoginForm from '@web/composition/useLoginForm'
import useErrors from '@web/composition/useErrors'

import { useRouter } from 'vue-router'
import { Form as VForm, Field as VField } from 'vee-validate'
import { useMeta } from '@web/common/vueMeta'
import { instructionUrl, isRegistrationEnable } from '@web/common/ExternalConfig/getters'

export default defineComponent({
  name: 'LoginForm',
  setup () {
    useMeta({
      title: i18n.global.t('Login')
    })
    const router = useRouter()

    const isShowSupportModal = ref(false)
    const isShowInstruction = ref(false)

    const {
      serverErrors,
      displayServerErrors,
      getFirstError
    } = useErrors()

    const {
      notice,
      ...loginForm
    } = useLoginForm({
      router,
      serverErrors,
      displayServerErrors,
      isLoadDataAfterSuccessLogin: false
    })

    function goTo (link) {
      router.push(link)
    }

    return {
      ...loginForm,
      isRegistrationEnable: isRegistrationEnable(),
      instructionUrl: instructionUrl(),
      isShowSupportModal,
      isShowInstruction,
      getFirstError,
      notice,
      goTo
    }
  },

  components: {
    AsModal,
    AsSupportModal,
    AsInput,
    AsNotice,
    AsDivider,
    AsTypesAuth,
    AsSupportAuth,
    VForm,
    VField
  }
})
</script>

<style lang="scss" scoped>
  .lAuth {
    &_form {
      .login_button + .login_button {
        margin-top: 16px;
      }
    }

    &_linkGroup {
      display: flex;
      align-items: center;
      flex-flow: column nowrap;
    }

    &_divider {
      margin-top: 20px;
      color: white;
      margin-bottom: 16px;
      max-width: 342px;
      width: 100%;
    }

    &_typesAuth {
      max-width: 342px;
      width: 100%;
    }

    &_register {
      margin-top: 16px;
      margin-bottom: 16px;
      text-transform: uppercase;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 19px;
    }

    &.-login &_logo {
      @media (max-height: 728px) {
        margin: 24px auto 16px;
      }
    }

    &.-login &_form {
      @media (max-height: 728px) {
        margin-top: 16px;
      }
    }
  }
</style>
